import React, { createContext, useContext, useState } from 'react';
import { Order } from 'models/order';
import { Organization } from 'models/organization';
import { User } from 'models/user';
import { SubcontractMessageField } from 'models/client-directory';
import { LocaleLanguage } from 'models/locale-format';
import { CONSTANTS } from 'config/constants';
import { DEFAULT_MESSAGE_FIELDS } from 'components/order-flow/subcontract-order/communication-step/subcontract-message';

/**
 * DD-3484 Marketplace: the subcontract flow's working state. Nothing is saved — no
 * draft, no mutation — until the confirm step submits, so abandoning the flow at any
 * step leaves no partial sub-order. The one exception is a scanner case imported in the
 * content step (DD-3771): it is attached to the parent right away and stays there.
 */

export type SubcontractSelection = {
    /** The parent `restorations[]._id`s that travel. */
    restorationIds: string[];
    /** Parent attachment file ids that travel. */
    fileIds: string[];
    /** Processed scanner-case file ids that travel (copied as plain attachments). */
    scannerCaseFileIds: string[];
    /** The patient details; a per-order decision, pre-filled from the partner defaults. */
    includePatientReference: boolean;
    /** The personal identity number, with the patient details. */
    includePatientIdentityNumber: boolean;
    /** The parent's dentist, as a line of text in the sub-order comment. */
    includeDentistName: boolean;
};

export type SubcontractOrderState = {
    supplier?: Organization;
    responsibleUser?: User;
    deliveryDate: Date | null;
    deliveryTime: { hour?: number; minute?: number };
    /** Set once the delivery date has been pre-filled from the external deadline (DD-3689). */
    isDeliveryDatePrefilled: boolean;
    /** Whether the delivery date shown is still the pre-filled deadline. */
    isDeliveryDateFromDeadline: boolean;
    selection: SubcontractSelection;
    /** The lab's message to the subcontractor; starts as the parent's internal notes (DD-3674). */
    comment: string;
    /** Set once the message has been pre-filled, so clearing it is not undone on the next visit. */
    isCommentPrefilled: boolean;
    /** DD-3689: the message last built from the selected parts, to tell an edited message apart. */
    generatedComment: string;
    /** DD-3689: the parts of the parent's communication the message is built from. */
    messageFields: SubcontractMessageField[];
    /** DD-3689: the language the message is translated into; null = the user's own. */
    messageLanguage: LocaleLanguage | null;
    /** DD-3689: the partner's responsible users to choose from; empty = every technician. */
    allowedResponsibleUserIds: string[];
    /** DD-3770: the sub-order's impression type, pre-filled from the parent; '' = none chosen. */
    sampleType: string;
};

type SubcontractOrderContextType = {
    parentOrder: Order;
    subcontract: SubcontractOrderState;
    updateSubcontract: (updates: Partial<SubcontractOrderState>) => void;
    updateSelection: (updates: Partial<SubcontractSelection>) => void;
    /** Re-reads the parent, e.g. after a scanner case has been attached to it (DD-3771). */
    refreshParentOrder: () => Promise<Order>;
};

export const SubcontractOrderContext = createContext<SubcontractOrderContextType>({
    parentOrder: {} as Order,
    subcontract: {} as SubcontractOrderState,
    updateSubcontract: () => {
        return;
    },
    updateSelection: () => {
        return;
    },
    refreshParentOrder: () => Promise.reject(new Error('No SubcontractOrderProvider')),
});

/** Nothing pre-selected — the state before a partner (and its defaults) is chosen. */
export const emptySubcontractSelection = (): SubcontractSelection => ({
    restorationIds: [],
    fileIds: [],
    scannerCaseFileIds: [],
    includePatientReference: false,
    includePatientIdentityNumber: false,
    includeDentistName: false,
});

/** DD-3770: the parent's impression type when it is one of the known ones, else none. */
export const parentSampleTypeOf = (parentOrder?: Order): string => {
    const type = parentOrder?.deliveryDetails?.sample?.type ?? '';
    return CONSTANTS.SAMPLE_METHODS.includes(type) ? type : '';
};

export const initialSubcontractState = (parentOrder?: Order): SubcontractOrderState => ({
    deliveryDate: null,
    deliveryTime: { hour: undefined, minute: undefined },
    isDeliveryDatePrefilled: false,
    isDeliveryDateFromDeadline: false,
    selection: emptySubcontractSelection(),
    comment: '',
    isCommentPrefilled: false,
    generatedComment: '',
    messageFields: DEFAULT_MESSAGE_FIELDS,
    messageLanguage: null,
    allowedResponsibleUserIds: [],
    sampleType: parentSampleTypeOf(parentOrder),
});

export const SubcontractOrderProvider = ({
    parentOrder,
    refreshParentOrder,
    children,
}: {
    parentOrder: Order;
    refreshParentOrder: () => Promise<Order>;
    children: React.ReactNode;
}) => {
    const [subcontract, setSubcontract] = useState<SubcontractOrderState>(() => initialSubcontractState(parentOrder));

    const updateSubcontract = (updates: Partial<SubcontractOrderState>) => {
        setSubcontract((previous) => ({ ...previous, ...updates }));
    };

    const updateSelection = (updates: Partial<SubcontractSelection>) => {
        setSubcontract((previous) => ({ ...previous, selection: { ...previous.selection, ...updates } }));
    };

    return (
        <SubcontractOrderContext.Provider
            value={{ parentOrder, subcontract, updateSubcontract, updateSelection, refreshParentOrder }}
        >
            {children}
        </SubcontractOrderContext.Provider>
    );
};

export const useSubcontractOrder = () => {
    const contextValue = useContext(SubcontractOrderContext);
    if (!contextValue) throw new Error('useSubcontractOrder must be used within SubcontractOrderProvider');
    return contextValue;
};
